import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { AUTH_ERRORS, AuthErrorModel, AuthErrorResponse, RedirectionError } from '@core/auth.model';
import { AuthUserModel, AuthUserViewModel, LoginModel } from '@core/user.model';
import { ACCESS_KEYS, AppConfig } from '@root/app.config';
import { NOT_FOUND } from 'http-status-codes';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { ALERT_MESSAGE, AlertService } from '../alert/alert.service';

export const TOKEN_KEY: string = 'accessTachomatToken';
@Injectable()
export class AuthService {
	private _loggedUser: BehaviorSubject<AuthUserViewModel | null> = new BehaviorSubject<AuthUserViewModel | null>(null);

	get loggedUser$(): Observable<AuthUserViewModel | null> {
		return this._loggedUser.asObservable();
	}

	get loggedUser(): AuthUserViewModel | null {
		return this._loggedUser.getValue();
	}

	get http(): HttpClient {
		return this.injector.get(HttpClient);
	}

	get alertService(): AlertService {
		return this.injector.get(AlertService);
	}

	constructor(
		private readonly injector: Injector,
		private readonly appConfig: AppConfig,
	) {
	}

	private removeToken(): void {
		localStorage.removeItem(ACCESS_KEYS);
		localStorage.removeItem(TOKEN_KEY);
	}

	getToken(): string | null {
		return localStorage.getItem(TOKEN_KEY);
	}

	public getTokenFromOsgi(cardNumber: string): Observable<string> {
		const osgiUrl: string = `${this.appConfig.api('osgiClientBase')}${this.appConfig.api('osgiGenerateTachomatToken')}`;
		let params = new HttpParams({});
		params = params.append('cardNumber', cardNumber);
		return this.http.get<string>(osgiUrl, { params }).pipe(
			map(value => { return value; })
			,
			catchError(error => {
				if (error instanceof HttpErrorResponse && error.status === NOT_FOUND) {
					if (this.shouldRetrieveOsgiToken()) {
						this.alertService.addMessage(ALERT_MESSAGE.DANGER, marker("tachomat.retrieve.osgi-token.404"));
						throw error;
					}
					this.alertService.addMessage(ALERT_MESSAGE.WARNING, marker("tachomat.retrieve.osgi-token.warning"));
					return of("");
				} else {
					this.alertService.addMessage(ALERT_MESSAGE.DANGER, marker("tachomat.retrieve.osgi-token.error"));
					throw error;
				}
			})
		);
	}

	private shouldRetrieveOsgiToken(): boolean {
		if (this.appConfig.hasProperty("tachomat.security.unsecureLimitDate")) {
			const unsecureLimitDateString: string = `${this.appConfig.getConfig('tachomat.security.unsecureLimitDate')}`;
			const unsecureLimitDate = moment.tz(unsecureLimitDateString, "UTC");
			if (unsecureLimitDate.isAfter(moment.now())) {
				return false;
			} else {
				return true;
			}
		} else {
			return true;
		}
	}

	private loginUser(data: LoginModel): Observable<AuthUserViewModel | null> {
		const tachoUrl: string = `${this.appConfig.api('baseUrl')}${this.appConfig.api('tachomat')}`;
		return this.http.post<AuthUserViewModel>(`${tachoUrl}${this.appConfig.api('tachomatLogin')}`, data).pipe(
			map((authUserModel: AuthUserModel | null) => {
				let userViewModel: AuthUserViewModel | null = null;

				if (authUserModel && authUserModel.token) {
					localStorage.setItem(TOKEN_KEY, authUserModel.token);
					userViewModel = {
						...authUserModel,
						...data
					};
					this._loggedUser.next(userViewModel);
				}
				return userViewModel;
			})
		);
	}

	login(data: LoginModel): Observable<AuthUserViewModel | null> {
		return this.getTokenFromOsgi(data.cardNumber).pipe(
			map(generatedToken => data.tokenFromOsgi = generatedToken),
			switchMap(() => {
				return this.loginUser(data)
					.pipe(
						catchError((errorResponse: AuthErrorResponse) => {
							const error: AUTH_ERRORS | RedirectionError = errorResponse.error;
							const mappedError: AuthErrorModel = { error: AUTH_ERRORS.UNKNOWN };
							if ((error as AUTH_ERRORS) in AUTH_ERRORS) {
								mappedError.error = error as AUTH_ERRORS;
							} else if (error && (error as RedirectionError).url) {
								mappedError.error = AUTH_ERRORS.ACCOUNT_NOT_EXISTS;
								mappedError.url = (error as RedirectionError).url;
							}
							throw mappedError;
						})
					);
			})
		);
	}

	logout(): Observable<Object | null> {
		const tachoUrl: string = `${this.appConfig.api('baseUrl')}${this.appConfig.api('tachomat')}`;
		if (this.getToken()) {
			return this.http.get(`${tachoUrl}${this.appConfig.api('tachomatLogout')}`).pipe(
				finalize(() => {
					this.removeToken();
					this._loggedUser.next(null);
				})
			);
		} else {
			this._loggedUser.next(null);
			return this._loggedUser;
		}
	}
}
